import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { FiltersService} from '../../services/filters.service';
import { Subscription } from 'rxjs';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { TemplateTranslationsService } from '../../services/template-translations.service';
import { PresentationType } from '../../types/presentation-types';
import { FilterValue } from 'src/app/features/filter-panel/types/base';
import { getAvailablePresentationConfig, PresentationConfig, TEMPLATE_TRANSLATIONS_NATIVE_FORM } from 'src/app/features/filter-panel/types/filters';
import { Division, Region } from '../../services/ebr-api-client/ebr-api-client.g';
import { regionDisplayNames } from 'src/app/global/types';

@Component({
    selector: 'presentation-options',
    templateUrl: './presentation-options.component.html',
    styleUrls: ['./presentation-options.component.scss'],
    standalone: false
})
export class PresentationOptionsComponent implements OnInit {
  @Output() onClose = new EventEmitter<void>();
  @Output() onProceed = new EventEmitter<{ template: string | undefined, translation: string | undefined }>();

  combinedConfig: Record<Region, {presentationConfig: PresentationConfig, translations: FilterValue[]}[]> = Object.values(Region).reduce((config, region) => {
    config[region] = [];
    return config;
  }, {} as Record<Region, {presentationConfig: PresentationConfig, translations: FilterValue[]}[]>);

  /** Configuration filtered by the selected division and regions */
  availablePresentationConfig: Record<Region, PresentationConfig[]> = Object.values(Region).reduce((config, region) => {
    config[region] = [];
    return config;
  }, {} as Record<Region, PresentationConfig[]>);

  // Page Views : 'Presentation Format' -> 'Template' -> 'Language'
  pageView: string = 'Presentation Format';
  presentationFormatSelectable: boolean = true;
  templateSelectable: boolean = true;
  languageSelectable: boolean = true;

  filters: ContractModel | undefined;
  filtersSubscription: Subscription | undefined;
  regionFilters: Region[] = [];

  translationOptions: FilterValue[] = [];

  isRegionSelected: boolean = false;
  isTemplateSelected: boolean = false;
  isLanguageSelected: boolean = false;

  selectedRegionCode: Region = Region.NA;
  selectedTemplateCode: PresentationType | undefined;
  selectedTranslationCode: string | undefined;

  //expose for HTML
  regionDisplayNames: Record<Region, string> = regionDisplayNames;
  // Mapping of translation codes to their native names for easy lookup in the template
  // Key: translation code, Value: native name of the translation
  translationNativeNames: Record<string, string> = TEMPLATE_TRANSLATIONS_NATIVE_FORM.reduce((names, t) => {
    names[t.code] = t.nativeName;
    return names;
  }, {} as Record<string, string>);

  constructor(
    private _filtersService: FiltersService,
    private _templateTranslationsService: TemplateTranslationsService
  ) { }


  ngOnInit(): void {
    this.filtersSubscription = this._filtersService.selectedFiltersObservable.subscribe(f => this.filters = f);
    if(this.filters?.division && this.filters?.regions){}

    if (this.filters) {
      // Set the initial page view based on the number of regions
      // Treat Distributor division as Region.NA
      this.regionFilters = this.filters.division as Division == Division.Distributor ? [Region.NA] : this.filters.regions as Region[];
      this.availablePresentationConfig = getAvailablePresentationConfig(this.filters.division as Division, this.regionFilters);
      
      //Create a larger configuration object that contains the available regions, the templates for each region, and the translations for each template.
      this.combinedConfig = Object.keys(this.availablePresentationConfig).reduce((config, region) => {
        config[region as Region] = this.availablePresentationConfig[region as Region].map(presentationConfig => ({
          presentationConfig,
          translations: this._templateTranslationsService.getAvailableTranslationsByTemplateCode(presentationConfig.presentationType)
        }));
        return config;
      }, {} as Record<Region, {presentationConfig: PresentationConfig, translations: FilterValue[]}[]>);

      //If multiple regions were selected, the user needs to select a region first before proceeding to template selection.
      //Otherwise, if only one region is selected, the user can proceed directly to either template or language selection.
      this.presentationFormatSelectable = this.regionFilters.length > 1;
      if(!this.presentationFormatSelectable){
        this.selectedRegionCode = this.regionFilters[0];
        this.isRegionSelected = true;
        
        this.templateSelectable = this.combinedConfig[this.selectedRegionCode as Region].length > 1;
        this.languageSelectable = this.combinedConfig[this.selectedRegionCode as Region].some(config => config.translations.length > 1);
  
        //If only one region and one templateCode but multiple languages...
        if(!this.templateSelectable && this.languageSelectable){
          this.selectedTemplateCode = this.combinedConfig[this.selectedRegionCode as Region][0].presentationConfig.presentationType;
          this.translationOptions = this.combinedConfig[this.regionFilters[0]][0].translations ?? [];
        }

      }

      // If multiple regions selected, default to 'Presentation Format' view to pick a region first.
      // Else, if only one region is selected: if there are multiple templates for that region, default to 'Template' view to pick a template.
      // Else, there is one region and one template but multiple translations available for that template.
      this.pageView = 
        this.presentationFormatSelectable ? 'Presentation Format' 
        : this.templateSelectable ? 'Template'
        : 'Language';
    } 
  }

  

  onRegionSelection(event: Event): void{
    const selectedValue = (event.target as HTMLInputElement).value;
    this.selectedRegionCode = selectedValue as Region;
    this.isRegionSelected = true;

    this.templateSelectable = this.combinedConfig[this.selectedRegionCode as Region].length > 1;
    this.languageSelectable = this.combinedConfig[this.selectedRegionCode as Region].some(config => config.translations.length > 1);

    if(!this.templateSelectable && !this.languageSelectable){
      this.selectedTemplateCode = this.combinedConfig[this.selectedRegionCode][0].presentationConfig.presentationType;
      this.selectedTranslationCode = this.combinedConfig[this.selectedRegionCode][0].translations[0].code;
    }
  }

  /**
   * Continues the flow from the region selection step to the next appropriate step (Template or Language) based on the availability of options.
   * Assumes that there are either multiple template options or translation options available for the region's presentation configuration.
   */
  continueFromRegionSelection(){
    if(this.selectedRegionCode && this.isRegionSelected){
      this.templateSelectable = this.combinedConfig[this.selectedRegionCode as Region].length > 1;
      this.languageSelectable = this.combinedConfig[this.selectedRegionCode as Region].some(config => config.translations.length > 1);
      
      if(this.templateSelectable){
        this.selectedTemplateCode = undefined;
        this.isTemplateSelected = false;
        this.pageView = 'Template'
      }
      else if(this.languageSelectable){
        this.selectedTemplateCode = this.combinedConfig[this.selectedRegionCode as Region][0].presentationConfig.presentationType;
        this.translationOptions = this._templateTranslationsService.getAvailableTranslationsByTemplateCode(this.selectedTemplateCode);
        this.selectedTranslationCode = undefined;
        this.isLanguageSelected = false;
        this.pageView = 'Language'
      }
    }
  }

  onTemplateSelection(event: Event): void {
    const selectedValue = (event.target as HTMLInputElement).value;
    
    if (!Object.values(PresentationType).includes(selectedValue as PresentationType)) {
      console.warn(`Invalid template code selected: ${selectedValue}`);
      return;
    }
    this.selectedTemplateCode = selectedValue as PresentationType;
    this.isTemplateSelected = true;

    this.translationOptions = this._templateTranslationsService.getAvailableTranslationsByTemplateCode(this.selectedTemplateCode);
    this.languageSelectable = this.translationOptions.length > 1;

  }


  onTranslationSelection(translation: Event){
    this.selectedTranslationCode = (translation.target as HTMLInputElement).value;
    this.isLanguageSelected = true;
  }

  /**
   * Navigates back from the language selection step to the previous appropriate step
   * (Template or Presentation Format) based on the availability of options.
   */
  navigateBackFromLanguageSelection(){
      this.selectedTranslationCode = undefined;
      this.templateSelectable = this.combinedConfig[this.selectedRegionCode as Region].length > 1;
      // this.languageSelectable = this.combinedConfig[this.selectedRegionCode as Region].some(config => config.translations.length > 1);
      if(this.templateSelectable){
        this.pageView = 'Template'
        return;
      }
      else if(this.presentationFormatSelectable){
        this.languageSelectable = true;
        this.pageView = 'Presentation Format'

      }
  }

  onProceedWithSelections(){
    this.onProceed.emit({
      template: this.selectedTemplateCode,
      translation: this.selectedTranslationCode
    });
  }
}
